import { Injectable, NgZone, OnDestroy } from '@angular/core';

/** Distance from a scroll container's visible edge, in px, where auto-scroll starts. */
const EDGE_SIZE = 80;
/** Scroll speed, in px per animation frame, with the pointer at the very edge. */
const MAX_SPEED = 24;
/** A drag that has not reported a position for this long has left the window. */
const STALE_AFTER_MS = 200;

/**
 * Scrolls the page, or the scroll container under the pointer, while a native HTML5 drag
 * holds the pointer near its top or bottom edge. Browsers barely auto-scroll during a native
 * drag, so a folder further down a long document tree could not be reached as a drop target.
 *
 * Root-provided and shared by every document tree on the page; each tree calls `attach()`
 * on creation and `detach()` on destroy, and the document listeners exist only while at
 * least one tree is attached. Only drags that start inside the page activate it — files
 * dragged in from the operating system never fire `dragstart` here.
 */
@Injectable({ providedIn: 'root' })
export class DragAutoScrollService implements OnDestroy {
  private attachCount = 0;
  private dragActive = false;
  private pointerY = 0;
  private pointerTarget: Element | null = null;
  private lastDragOverAt = 0;
  private frameId: number | null = null;

  constructor(private zone: NgZone) {}

  attach(): void {
    this.attachCount++;
    if (this.attachCount === 1) {
      this.zone.runOutsideAngular(() => {
        document.addEventListener('dragstart', this.onDragStart, true);
        document.addEventListener('dragover', this.onDragOver, true);
        document.addEventListener('dragend', this.onDragStop, true);
        document.addEventListener('drop', this.onDragStop, true);
      });
    }
  }

  detach(): void {
    if (this.attachCount === 0) {
      return;
    }
    this.attachCount--;
    if (this.attachCount === 0) {
      this.removeListeners();
    }
  }

  ngOnDestroy(): void {
    this.removeListeners();
  }

  private removeListeners(): void {
    document.removeEventListener('dragstart', this.onDragStart, true);
    document.removeEventListener('dragover', this.onDragOver, true);
    document.removeEventListener('dragend', this.onDragStop, true);
    document.removeEventListener('drop', this.onDragStop, true);
    this.onDragStop();
  }

  private onDragStart = (): void => {
    this.dragActive = true;
  };

  // dragover keeps firing roughly every 50 ms even while the pointer rests at the edge,
  // so it doubles as the "still dragging here" signal for the animation loop.
  private onDragOver = (event: DragEvent): void => {
    if (!this.dragActive) {
      return;
    }
    this.pointerY = event.clientY;
    this.pointerTarget = event.target instanceof Element ? event.target : null;
    this.lastDragOverAt = performance.now();
    if (this.frameId === null) {
      this.frameId = requestAnimationFrame(this.tick);
    }
  };

  private onDragStop = (): void => {
    this.dragActive = false;
    this.pointerTarget = null;
    if (this.frameId !== null) {
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
  };

  private tick = (): void => {
    this.frameId = null;
    if (
      !this.dragActive ||
      performance.now() - this.lastDragOverAt > STALE_AFTER_MS
    ) {
      return;
    }
    this.scrollStep();
    this.frameId = requestAnimationFrame(this.tick);
  };

  /**
   * Scrolls the innermost container under the pointer whose edge the pointer is near and
   * which can still scroll that way, falling back to the page itself.
   */
  private scrollStep(): void {
    for (
      let el = this.pointerTarget;
      el !== null && el !== document.body && el !== document.documentElement;
      el = el.parentElement
    ) {
      if (!this.isScrollable(el)) {
        continue;
      }
      const rect = el.getBoundingClientRect();
      const top = Math.max(rect.top, 0);
      const bottom = Math.min(rect.bottom, window.innerHeight);
      const delta = this.getDelta(
        top,
        bottom,
        el.scrollTop,
        el.scrollHeight - el.clientHeight
      );
      if (delta !== 0) {
        el.scrollTop += delta;
        return;
      }
    }
    const page = document.documentElement;
    const delta = this.getDelta(
      0,
      window.innerHeight,
      page.scrollTop,
      page.scrollHeight - page.clientHeight
    );
    if (delta !== 0) {
      page.scrollTop += delta;
    }
  }

  /** Pixels to scroll this frame for a visible area spanning `top`–`bottom`; 0 for none. */
  private getDelta(
    top: number,
    bottom: number,
    scrollTop: number,
    maxScrollTop: number
  ): number {
    const edge = Math.min(EDGE_SIZE, (bottom - top) / 3);
    if (edge <= 0) {
      return 0;
    }
    const y = this.pointerY;
    if (y >= top && y < top + edge && scrollTop > 0) {
      return -this.speed((top + edge - y) / edge);
    }
    if (y <= bottom && y > bottom - edge && scrollTop < maxScrollTop) {
      return this.speed((y - (bottom - edge)) / edge);
    }
    return 0;
  }

  /** Eases in, so the edge of the zone scrolls gently and the very edge scrolls fast. */
  private speed(depth: number): number {
    const clamped = Math.min(Math.max(depth, 0), 1);
    return Math.max(1, Math.round(MAX_SPEED * clamped * clamped));
  }

  private isScrollable(el: Element): boolean {
    if (el.scrollHeight <= el.clientHeight) {
      return false;
    }
    const overflowY = getComputedStyle(el).overflowY;
    return (
      overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay'
    );
  }
}
